import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { type PropsValue } from 'react-select';

import { type OptionType } from '@hivebrite/design-system/Inputs';

import { convertToOptions } from '../utils/convertToOptions';
import { extractIds } from '../utils/extractIds';
import { isAlreadyOptionType } from '../utils/isAlreadyOptionType';
import { resolveOptionsFromIds } from '../utils/resolveOptionsFromIds';

interface QueryFnParams {
  ids?: Array<number>;
  searchText?: string;
}

interface UseReactSelectValueAsOptionsProps<
  TQueryFnData,
  TData extends object,
> {
  dataToOptions: (data: Array<TData>) => Array<OptionType>;
  defaultOptions?: Array<OptionType>;
  enabled?: boolean;
  extractData: (response: TQueryFnData) => Array<TData>;
  queryFn: (
    params: QueryFnParams,
    signal: AbortSignal,
  ) => Promise<TQueryFnData>;
  queryKey: Array<unknown>;
  staleTime?: number;
  value?:
    | Array<number>
    | Array<TData>
    | PropsValue<OptionType>
    | TData
    | number
    | null;
}

/**
 * Generic hook that converts IDs to option objects for ReactSelectAdaptive components.
 * If the value provided is already an option object, it will be returned as is.
 * If the value is an id or an array of ids, the data will be fetched and converted to option objects.
 * @param queryKey - Query key for react-query
 * @param value - Value to fetch the options for
 * @param enabled - Whether the query should be enabled
 * @param queryFn - Function to query the data
 * @param extractData - Function to extract data from the query response
 * @param dataToOptions - Function to convert the fetched data to option objects
 * @param staleTime - Stale time for react-query
 * @returns {Object} - Object containing the options, the loading state, and the function to add options to the cache
 */
export const useReactSelectValueAsOptions = <
  TQueryFnData,
  TData extends object,
>({
  queryKey,
  value,
  defaultOptions = [],
  enabled = true,
  queryFn,
  extractData,
  dataToOptions,
  staleTime,
}: UseReactSelectValueAsOptionsProps<TQueryFnData, TData>) => {
  // We maintain a cache of existing options in order to easily convert ids to options
  // without having to fetch the data again.
  const [optionsCache, setOptionsCache] =
    useState<Array<OptionType>>(defaultOptions);

  const addOptionsToCache = useCallback((fetchedOptions: Array<OptionType>) => {
    setOptionsCache((prev) => {
      const optionsToAdd = fetchedOptions.filter(
        (option) =>
          !prev.some((prevOption) => prevOption.value === option.value),
      );

      if (optionsToAdd.length > 0) {
        return [...prev, ...optionsToAdd];
      }

      return prev;
    });
  }, []);

  useEffect(() => {
    if (defaultOptions.length > 0) {
      addOptionsToCache(defaultOptions);
    }
  }, [defaultOptions, addOptionsToCache]);

  const { isLoading, data: valueAsOptions = [] } = useQuery<
    PropsValue<OptionType>
  >({
    queryKey: [...queryKey, value],
    enabled,
    queryFn: async () => {
      // Early return for empty values
      if (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return [];
      }

      // If value is already in OptionType format, return as is
      if (isAlreadyOptionType(value)) {
        return value;
      }

      // Extract IDs from value (handles both single ID and array of IDs)
      const ids = extractIds(value);

      // If we have IDs, resolve them from cache or fetch missing ones
      if (ids.length > 0) {
        const allAvailableOptions = [...defaultOptions, ...optionsCache];
        return await resolveOptionsFromIds(
          ids,
          allAvailableOptions,
          queryFn,
          (response) => dataToOptions(extractData(response)),
          addOptionsToCache,
        );
      }

      // Otherwise, convert TData to OptionType
      return convertToOptions(value as Array<TData> | TData, dataToOptions);
    },
    staleTime,
  });

  return {
    isLoading,
    valueAsOptions,
    optionsCache,
    addOptionsToCache,
  };
};
